import React, { useContext, useState, useEffect, useCallback } from "react";
import Context from "../../Context";
import Blocker from "./Blocker";
import ListPagination from "./ListPagination";
import DefaultNotFound from "./DefaultNotFound";
import unique from "array-unique";

function List(props) {
  const cx = useContext(Context);
  const [items, updateItems] = useState([]);
  const [selectedItems, updateSelectedItems] = useState([]);
  const [pending, setPending] = useState(false);
  const [pagination, setPagination] = useState({
    limit: props.hasOwnProperty("limit") ? props.limit : null,
    page: 1,
    pages: [],
  });

  const [shifPressed, setShiftPressed] = useState(false);

  let itemsListArray = [];
  if (Array.isArray(items)) {
    itemsListArray = items;
  } else {
    if (items.hasOwnProperty("id")) {
      itemsListArray = [items];
    }
  }

  useEffect(() => {
    pagination.page = 1;
    setPagination(pagination);
    cx.api.lastCallCache[props.listKey] = props.search;
    refresh();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.search, props.sort, props.fields, props.fulltext]);

  const onKeyUp = useCallback((e) => {
    if (e?.key === "Shift") setShiftPressed(false);
  }, []);

  const onKeyDown = useCallback(
    (e) => {
      if (e?.key === "Shift") setShiftPressed(true);
      if (e?.ctrlKey && e?.key === "a") {
        e.preventDefault();

        updateSelectedItems(itemsListArray.map((i) => i.id));
      } // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [itemsListArray]
  );

  useEffect(() => {
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onKeyUp, onKeyDown]);

  const search = props.search ? JSON.parse(props.search) : [];
  const sort = props.sort ? JSON.parse(props.sort) : [];
  const fields = props.fields ? JSON.parse(props.fields) : [];
  const fulltext = props.fulltext ? JSON.parse(props.fulltext) : [];
  const customArgs = props.customArgs ? JSON.parse(props.customArgs) : {};

  function paginationBuild() {
    pagination.pages =
      pagination.limit && pagination.total
        ? Math.ceil(pagination.total / pagination.limit)
        : 0;
  }

  function paginationLimitChange(limit) {
    if (limit) {
      pagination.limit = limit;
      refresh();
    }
  }

  function paginationPageChange(page) {
    if (page > 0 && page <= pagination.pages) {
      pagination.page = page;
      refresh();
    }
  }

  async function count() {
    if (pagination.limit) {
      await cx.api
        .tfetch(props.path.replace("/list", "/count"), {
          search: search || fulltext ? search.concat(fulltext) : [],
          ...customArgs,
        })
        .then(
          (json) => {
            if (props.search === cx.api.lastCallCache[props.listKey]) {
              if (json.hasOwnProperty("error")) {
                if (!props.quiet) cx.toast(json.error["message"], "warning");
                setPagination({});
              } else {
                pagination.total = json["count"];
                setPagination(pagination);
              }
            }
          },
          (e) => {
            if (!props.quiet) cx.toast(e.message, "warning");
          }
        );
      paginationBuild();
    }
  }

  async function refresh() {
    if (props.path) {
      setPending(true);
      if (props.onLoadEnd) props.onLoadStart();

      let queryParams = {
        reclimit: pagination.limit,
        recoffset: (pagination.page - 1) * pagination.limit,
        search: search.concat(fulltext),
        orderby: sort,
        ...customArgs,
      };

      if (props.fields) {
        queryParams.fields = fields;
      }

      await count();

      updateSelectedItems([]);

      let items = await cx.api.qa(props.path, queryParams);
      if (props.search === cx.api.lastCallCache[props.listKey]) {
        updateItems(items);
      }

      if (props.search === cx.api.lastCallCache[props.listKey]) {
        setPending(false);
        if (props.onLoadEnd) props.onLoadEnd();
      }
    }
  }

  let Item = props.children ? (
    React.Children.only(props.children)
  ) : (
    <DefaultItem />
  );

  let firstItem =
    typeof itemsListArray[0] === "object" ? itemsListArray[0] : false;
  let itemsCols = firstItem ? Object.keys(firstItem) : [];

  function onItemClick(e, listItemId) {
    const ctrl = e.ctrlKey;
    const shift = e.shiftKey;
    const currItemId = props.currentItem;

    if (props.multiselect) {
      let newSelectedItems = [...selectedItems];
      let lastSelectedItemId = newSelectedItems[newSelectedItems.length - 1];

      if (ctrl || shift) {
        e.preventDefault();

        if (ctrl) {
          if (selectedItems.includes(listItemId)) {
            newSelectedItems = newSelectedItems.filter(
              (id) => id !== listItemId
            );
          } else {
            newSelectedItems.push(listItemId);
          }
          if (currItemId) {
            newSelectedItems.push(currItemId);
          }
        }

        if (shift && !ctrl) {
          newSelectedItems = [];

          let firstItemId = currItemId || lastSelectedItemId;

          let startIndex = itemsListArray.findIndex(
            (i) => i.id === firstItemId
          );
          let endIndex = itemsListArray.findIndex((i) => i.id === listItemId);
          if (endIndex < startIndex) {
            [endIndex, startIndex] = [startIndex, endIndex];
          }
          newSelectedItems = itemsListArray
            .slice(startIndex, endIndex + 1)
            .map((i) => i.id);
        }
      } else {
        if (!currItemId) {
          newSelectedItems = [listItemId];
        } else {
          newSelectedItems = [];
        }
      }
      updateSelectedItems(unique(newSelectedItems));
    }
  }

  function renderItems() {
    return (
      <>
        {itemsListArray.map((item, itemIndex) => {
          let listItemId = item[props.keyField] || itemIndex;
          return React.cloneElement(Item, {
            ...item,
            key: listItemId,
            listItemId,
            selectedItems,
            onItemClick,
            onPending: setPending,
          });
        })}
      </>
    );
  }

  let itemsList = null;
  if (props.table) {
    let headings = props.tableHeadings || (
      <DefaultTableHeadings cols={itemsCols} />
    );
    itemsList = (
      <div className="table-responsive">
        <table className="table  w-100">
          {headings}
          <tbody>{renderItems()}</tbody>
        </table>
      </div>
    );
  } else if (props.row) {
    itemsList = <div className="row">{renderItems()}</div>;
  } else {
    itemsList = renderItems();
  }

  const notFoundItem = props.notFoundItem || <DefaultNotFound />;
  let blockerProps = props.blocker || {};

  let containerClasses = [];
  if (shifPressed) containerClasses.push("noselect");

  return (
    <>
      <Blocker {...blockerProps} on={pending}>
        <div className={containerClasses.join(" ")}>
          {itemsList}
          {itemsListArray.length === 0 && !pending ? notFoundItem : null}

          {pagination.pages > 1 || !props.hideSinglePageNav ? (
            <ListPagination
              limitChangeFn={(limit) => paginationLimitChange(limit)}
              changeFn={(page) => paginationPageChange(page)}
              pagination={pagination}
              page={pagination.page}
            />
          ) : null}
        </div>
      </Blocker>
    </>
  );
}

function DefaultItem(props) {
  const cx = useContext(Context);
  let values = [];
  for (let field in props) values.push(field);
  function explain(json) {
    cx.say(json);
  }
  return (
    <tr>
      {values.map((v) => {
        let value = props[v];
        let date = new Date(value);

        if (typeof value === "object") {
          let json = JSON.stringify(value);
          if (json === "null") {
            value = "-";
          } else {
            value = (
              <span
                className="text-muted text-hover-primary btn"
                onClick={() => explain(json)}
              >
                {json.length > 20 ? json.substring(0, 20) + "..." : json}
              </span>
            );
          }
        }
        if (date.getFullYear() > 1971) {
          value = date.toLocaleString();
        }
        return (
          <td key={v} className="text-nowrap">
            {value}
          </td>
        );
      })}
    </tr>
  );
}

function DefaultTableHeadings(props) {
  return (
    <thead>
      <tr>
        {props.cols.map((v) => (v !== "key" ? <th key={v}>{v}</th> : null))}
      </tr>
    </thead>
  );
}

export default List;
